import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Clock, MapPin, Users, Phone, User, Check, Calendar as CalendarIcon, X, Mail, DollarSign, CreditCard, ShieldAlert, AlertCircle } from 'lucide-react';
import { db, createUserProfile, functions } from '../firebase';
import { collection, addDoc, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from './CheckoutForm';
import { useAppContext } from '../App';
import { useTranslation } from 'react-i18next';
import { 
  getCancellationDeadline, 
  formatDeadline, 
  calculateHoursUntilBooking 
} from '../utils/cancellationPolicy';

// Initialize Stripe outside of component
const stripePromise = loadStripe("pk_test_51SrfeyIf7yVAAcepcZefP1yiQ1KPP36jNVKxGrw4lBZDMEcrOHq7pGaksIWHbnLKQye4LLJTuw1rV4L99gr4E0Vw00C0ClPCXm"); // Replace with your actual publishable key

const BookingPage = ({ stadiumId = 'green-field-stadium', sportId = null, user }) => {
  const { setShowLoginModal, setBookingIntent, bookingIntent } = useAppContext();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language; 
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: '',
    phone: '',
    email: '',
    players: '',
    paymentType: 'cash'
  });
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingState, setBookingState] = useState('form');
  const [bookingError, setBookingError] = useState('');
  const [stadiumSettings, setStadiumSettings] = useState({
    name: 'Loading...',
    location: 'Loading...',
    pricePerSlot: 20,
    amenities: [],
    photos: ['https://images.unsplash.com/photo-1529900748604-07564a03e7a6?w=800']
  });
  const [selectedSport, setSelectedSport] = useState(null);
  const [clientSecret, setClientSecret] = useState("");

  // --- THIS IS THE FIX for the "Cannot access before initialization" crash ---
  const handleCloseModal = useCallback(() => {
    setShowBookingForm(false);
    setSelectedSlot(null);
    setBookingData({ name: '', phone: '', email: '', players: '', paymentType: 'cash' });
    setBookingState('form');
    setBookingError('');
    // Re-enable body scroll
    document.body.style.overflow = 'unset';
  }, []);

  // Load stadium settings
  useEffect(() => {
    const loadSettings = async () => {
      if (!stadiumId) return;
      try {
        const docRef = doc(db, 'stadiums', stadiumId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setStadiumSettings(data);
          // Set currency from stadium settings or default to USD
          // This ensures we respect the stadium's currency setting throughout the flow
          if (data.currency) {
            // Store currency to be used in payment intent
             console.log("Stadium currency:", data.currency);
          }
          if (sportId && data.sports) {
            const sport = data.sports.find(s => s.id === sportId);
            if (sport) {
              setSelectedSport(sport);
              setStadiumSettings(prev => ({
                ...prev,
                pricePerSlot: sport.pricePerSlot
              }));
            }
          }
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, [stadiumId, sportId]);

  // Generate all time slots for the selected sport
  const [timeSlots, setTimeSlots] = useState([]);
  const generateTimeSlots = (sport) => {
    const slots = [];
    if (!sport || !sport.openTime || !sport.closeTime || !sport.duration) return slots;
    const durationMin = parseInt(sport.duration);
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };
    const minutesToTime = (totalMinutes) => {
      const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
      const minutes = (totalMinutes % 60).toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };
    const openMin = timeToMinutes(sport.openTime);
    const closeMin = timeToMinutes(sport.closeTime);
    let currentMin = openMin;
    let slotIndex = 0;
    while (currentMin + durationMin <= closeMin) {
      const startTime = minutesToTime(currentMin);
      const endTime = minutesToTime(currentMin + durationMin);
      const slotId = `${sport.id}-slot-${slotIndex}`;
      slots.push({ id: slotId, startTime, endTime, price: sport.pricePerSlot, isBooked: false });
      currentMin += durationMin;
      slotIndex++;
    }
    return slots;
  };

  useEffect(() => {
    if (selectedSport) {
      const slots = generateTimeSlots(selectedSport);
      setTimeSlots(slots);
    }
  }, [selectedSport]);

  // --- THIS IS THE NEW, SIMPLE BOOKING LOADER ---
  // It only fetches from the 'bookings' collection. No index errors.
  useEffect(() => {
    if (!selectedSport || !sportId) return; // Don't run without a sport

    const loadAllBookings = async () => {
      setLoading(true);
      try {
        const dateString = selectedDate.toISOString().split('T')[0];
        const allBookedSlotIds = new Set();

        // 1. Get all bookings for this sport on this day
        const q = query(
          collection(db, 'bookings'),
          where('date', '==', dateString),
          where('sportId', '==', sportId)
        );
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          const bookingData = doc.data();
          // Only mark slot as booked if booking is not cancelled
          if (bookingData.status !== 'cancelled') {
            allBookedSlotIds.add(bookingData.slotId);
          }
        });
        
        // 2. Set the final list
        setBookedSlots(Array.from(allBookedSlotIds));

      } catch (error) {
        console.error('Error loading all bookings:', error);
      }
      setLoading(false);
    };

    // We only run this *after* the time slots have been generated
    if (timeSlots.length > 0) {
      loadAllBookings();
    }
  }, [selectedDate, stadiumId, sportId, selectedSport, timeSlots]); // Runs when date or timeSlots change

  // This effect handles the "Enter" key for the success modal
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Enter') {
        if (bookingState === 'success') {
          handleCloseModal();
        }
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [bookingState, handleCloseModal]); // handleCloseModal is now stable

  // --- THIS IS THE FIX for the "auto-open" bug ---
  useEffect(() => {
    if (user && user.phoneNumber && bookingIntent && !showBookingForm) {
      setBookingData({ 
        ...bookingData, 
        phone: user.phoneNumber,
        name: user.name || user.displayName || '', 
        email: user.email || '' 
      });
      setSelectedSlot(bookingIntent);
      setShowBookingForm(true);
      setBookingIntent(null);
    }
  }, [user, bookingIntent, showBookingForm, setBookingIntent, bookingData]);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (showBookingForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showBookingForm]);

  const displaySlots = timeSlots.map(slot => {
    const isBooked = bookedSlots.includes(slot.id);
    
    // Check if slot time has passed for today
    let isPastTime = false;
    const isToday = selectedDate.toDateString() === new Date().toDateString();
    if (isToday) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTotalMinutes = currentHour * 60 + currentMinute;
      
      // Parse slot start time
      const [slotHour, slotMinute] = slot.startTime.split(':').map(Number);
      const slotTotalMinutes = slotHour * 60 + slotMinute;
      
      // If the slot start time has passed, mark it as past
      if (slotTotalMinutes <= currentTotalMinutes) {
        isPastTime = true;
      }
    }
    
    return {
      ...slot,
      price: selectedSport.pricePerSlot, // Use the correct sport's price
      isBooked: isBooked || isPastTime // Treat past times as "booked" (unavailable)
    };
  });

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return currentLang === 'ar' 
      ? date.toLocaleDateString('ar-LB', options)
      : date.toLocaleDateString('en-US', options);
  };

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 14);
    if (newDate >= new Date(new Date().setHours(0,0,0,0)) && newDate <= maxDate) {
      setSelectedDate(newDate);
    }
  };

  const handleSlotClick = (slot) => {
    if (!slot.isBooked) {
      setSelectedSlot(slot); 
      if (user && user.phoneNumber) {
        setBookingData({ 
          ...bookingData, 
          phone: user.phoneNumber,
          name: user.name || user.displayName || '', 
          email: user.email || '' 
        });
        setShowBookingForm(true);
      } else {
        setBookingIntent(slot); // Save the intent
        setShowLoginModal(true); // Open the global modal
      }
    }
  };

  // Create Payment Intent when entering payment state
  useEffect(() => {
    if (bookingState === 'payment' && selectedSlot && !clientSecret && stadiumSettings.onlinePaymentRequired) {
      const createPaymentIntent = httpsCallable(functions, 'createPaymentIntent');
      
      const policy = stadiumSettings.cancellationPolicy || {};
      const isDeposit = policy.type === 'partial-deposit';
      const depositAmount = isDeposit && policy.depositPercentage 
          ? Math.ceil(selectedSlot.price * (policy.depositPercentage / 100)) 
          : selectedSlot.price;

      createPaymentIntent({ 
        amount: depositAmount, 
        currency: 'usd', 
        description: `Booking for ${stadiumSettings.name}`,
        stadiumId: stadiumId,
        sportId: sportId,
        customerEmail: user?.email || bookingData.email
      })
      .then((result) => {
        setClientSecret(result.data.clientSecret);
      })
      .catch((error) => {
        console.error("Error creating payment intent:", error);
        setBookingError(t('bookingpage.errorPaymentInit', 'Could not initialize payment.'));
      });
    }
  }, [bookingState, selectedSlot, clientSecret, stadiumSettings, t, stadiumId, sportId, user, bookingData.email]);
  
  const createBookingInFirestore = async (paymentType, transactionId = null, paymentStatus = 'paid') => {
      console.log("Creating booking in Firestore...", { paymentType, transactionId, paymentStatus });
      const dateString = selectedDate.toISOString().split('T')[0];
      try {
        await addDoc(collection(db, 'bookings'), {
            stadiumId: stadiumId,
            sportId: sportId,
            sportName: selectedSport?.name || '',
            stadiumName: stadiumSettings.name,
            date: dateString,
            slotId: selectedSlot.id,
            startTime: selectedSlot.startTime,
            endTime: selectedSlot.endTime,
            customerName: bookingData.name,
            customerPhone: bookingData.phone,
            customerEmail: bookingData.email,
            numPlayers: parseInt(bookingData.players),
            paymentType: paymentType, // 'cash' or 'online'
            transactionId: transactionId, // Add transaction ID
            paymentStatus: paymentStatus, // 'pending', 'paid'
            price: selectedSlot.price,
            createdAt: new Date().toISOString(),
            status: 'confirmed',
            userId: user.uid,
            type: 'customer'
        });
        console.log("Booking document created successfully.");
        
        const profileData = {
            phoneNumber: user.phoneNumber,
            name: bookingData.name,
            email: bookingData.email,
        };
        await createUserProfile(user.uid, profileData);
        
        setBookingState('success');
        setBookedSlots([...bookedSlots, selectedSlot.id]);
      } catch (error) {
        console.error("Error creating booking in Firestore:", error);
        throw error; // Re-throw to be caught by caller
      }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
     setBookingState('submitting');
     try {
       // SECURITY UPGRADE: Verify payment on backend instead of creating doc directly
       // This prevents "fake paid" bookings
       const verifyPaymentAndCreateBooking = httpsCallable(functions, 'verifyPaymentAndCreateBooking');
       
       // Prepare data for the backend
       // Note: We don't include status/paymentStatus as the backend sets those
       const bookingPayload = {
            stadiumId: stadiumId,
            sportId: sportId,
            sportName: selectedSport?.name || '',
            stadiumName: stadiumSettings.name,
            date: selectedDate.toISOString().split('T')[0],
            slotId: selectedSlot.id,
            startTime: selectedSlot.startTime,
            endTime: selectedSlot.endTime,
            customerName: bookingData.name,
            customerPhone: bookingData.phone,
            customerEmail: bookingData.email,
            numPlayers: parseInt(bookingData.players),
            paymentType: 'online',
            price: selectedSlot.price
       };

       await verifyPaymentAndCreateBooking({
         paymentIntentId: paymentIntent.id,
         bookingData: bookingPayload
       });
       
       // Update profile (safe to do client side as it doesn't affect money)
        const profileData = {
            phoneNumber: user.phoneNumber,
            name: bookingData.name,
            email: bookingData.email,
        };
       await createUserProfile(user.uid, profileData);
       setBookedSlots([...bookedSlots, selectedSlot.id]);

       setBookingState('success'); 
     } catch (error) {
       console.error("Secure booking creation failed:", error);
       setBookingError(t('bookingpage.errorPaymentVerify', 'Payment successful but booking failed. Please contact support.'));
       setBookingState('form');
     }
  };

  const handleSubmit = async (event) => {
    event.preventDefault(); // <-- FIX for "Enter" key
    
    // Field-specific validation with clear error messages
    if (!bookingData.name || !bookingData.phone || !bookingData.email || !bookingData.players) {
      const missingFields = [];
      if (!bookingData.name) missingFields.push(t('bookingpage.formFullName'));
      if (!bookingData.email) missingFields.push(t('bookingpage.formEmail'));
      if (!bookingData.phone) missingFields.push(t('bookingpage.formPhone'));
      if (!bookingData.players) missingFields.push(t('bookingpage.formPlayers'));
      
      const errorMessage = currentLang === 'ar' 
        ? `يرجى ملء الحقول المطلوبة: ${missingFields.join(' و ')}`
        : `Please fill in: ${missingFields.join(', ')}`;
      
      setBookingError(errorMessage);
      return;
    }
    setBookingError('');

    // Check if online payment is required
    if (stadiumSettings.onlinePaymentRequired) {
        setBookingState('payment');
        return;
    }

    // Default to 'pay-at-venue' payment status for cash bookings
    setBookingState('submitting');
    try {
        await createBookingInFirestore('pay-at-venue', null, 'pending');
    } catch (error) {
      console.error('Error saving booking:', error);
      setBookingError(t('bookingpage.formErrorSubmit'));
      setBookingState('form');
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-amber-50 via-white to-yellow-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 transition-colors ${currentLang === 'ar' ? 'text-right' : 'text-left'}`} dir={currentLang === 'ar' ? 'rtl' : 'ltr'}>
      
      {/* The global <Header> from App.js renders here */}

      {/* Revised Header Section - No Background Image, just clean info */}
      <div className="pt-24 pb-8 bg-gradient-to-b from-white to-amber-50 dark:from-neutral-900 dark:to-neutral-900/50">
        <div className="max-w-6xl mx-auto px-6 text-center">
            
            <div className="inline-block p-4 rounded-full bg-primary/10 dark:bg-primary/20 mb-6 animate-fade-in-up">
                 <div className="text-primary dark:text-primary-400 font-bold tracking-wider uppercase text-sm">{t('bookingpage.heroTitle')}</div>
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold mb-4 text-neutral-800 dark:text-white animate-fade-in-up drop-shadow-sm">
                {stadiumSettings.name}
            </h1>
            
            <div className="flex items-center justify-center gap-2 text-lg md:text-xl text-neutral-600 dark:text-neutral-400 mb-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <MapPin size={22} className="text-primary" />
              <span>{stadiumSettings.location}</span>
            </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        
        {/* Dynamic Policy Card - Replaces 3 Feature Cards */}
        <div className="mb-12 animate-fade-in-up">
             {(() => {
                const policy = stadiumSettings.cancellationPolicy || {
                    type: 'pay-at-venue',
                    cancellationWindow: 24,
                    lateCancellationFee: 0,
                    refundPercentage: 100,
                    lateRefundPercentage: 0
                };
                
                const isFullPayment = policy.type === 'full-payment';
                const isDeposit = policy.type === 'partial-deposit';
                
                let title = currentLang === 'ar' ? 'سياسة الحجز والدفع' : 'Booking & Payment Policy';
                
                return (
                    <div className="glass-card rounded-3xl p-8 border border-primary/20 bg-gradient-to-br from-white to-blue-50/50 dark:from-neutral-800 dark:to-neutral-800/50 relative overflow-hidden shadow-luxury">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
                        
                        <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
                            {/* Policy Icon */}
                            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                {policy.type === 'pay-at-venue' || !stadiumSettings.onlinePaymentRequired ? (
                                    <DollarSign size={36} className="text-primary" />
                                ) : (
                                    <CreditCard size={36} className="text-primary" />
                                )}
                            </div>

                            <div className="flex-1 text-center md:text-left rtl:md:text-right">
                                <h3 className="text-2xl font-bold text-neutral-800 dark:text-white mb-3 flex items-center justify-center md:justify-start rtl:md:justify-end gap-3">
                                    {title}
                                    <span className="text-sm font-semibold px-3 py-1 bg-primary/10 text-primary rounded-full">
                                        {currentLang === 'ar' 
                                            ? ((policy.type === 'pay-at-venue' || !stadiumSettings.onlinePaymentRequired) ? 'الدفع في الملعب' : (isFullPayment ? 'دفع كامل' : 'عربون'))
                                            : ((policy.type === 'pay-at-venue' || !stadiumSettings.onlinePaymentRequired) ? 'Pay at Venue' : (isFullPayment ? 'Full Payment' : 'Deposit'))
                                        }
                                    </span>
                                </h3>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-neutral-600 dark:text-neutral-300">
                                    <div className="flex items-center gap-3 bg-white/50 dark:bg-neutral-900/50 p-3 rounded-xl">
                                        <Clock size={20} className="text-primary flex-shrink-0" />
                                        <span className="text-sm">
                                            {currentLang === 'ar' 
                                                ? `إلغاء مجاني قبل ${policy.cancellationWindow} ساعة`
                                                : `Free cancellation ${policy.cancellationWindow}h before`
                                            }
                                        </span>
                                    </div>
                                    
                                    {(stadiumSettings.onlinePaymentRequired && (isFullPayment || isDeposit)) && (
                                        <div className="flex items-center gap-3 bg-white/50 dark:bg-neutral-900/50 p-3 rounded-xl">
                                            <ShieldAlert size={20} className="text-orange-500 flex-shrink-0" />
                                            <span className="text-sm">
                                                {currentLang === 'ar' 
                                                    ? `استرداد ${policy.lateRefundPercentage || 0}% للإلغاء المتأخر`
                                                    : `Late cancel: ${policy.lateRefundPercentage || 0}% refund`
                                                }
                                            </span>
                                        </div>
                                    )}

                                     {stadiumSettings.onlinePaymentRequired && isDeposit && (
                                        <div className="flex items-center gap-3 bg-white/50 dark:bg-neutral-900/50 p-3 rounded-xl md:col-span-2">
                                            <AlertCircle size={20} className="text-blue-500 flex-shrink-0" />
                                            <span className="text-sm">
                                                {currentLang === 'ar' 
                                                    ? `مطلوب عربون ${policy.depositPercentage}% لتأكيد الحجز`
                                                    : `${policy.depositPercentage}% deposit required to confirm`
                                                }
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>

        {/* Date Selector - Luxury Glass Card */}
        <div className="glass-card rounded-3xl shadow-luxury p-8 md:p-10 mb-8 border border-white/20 dark:border-neutral-700/30 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 dark:text-white mb-8 flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-400/20 to-accent-500/20 rounded-2xl flex items-center justify-center backdrop-blur-xl">
              <CalendarIcon className="text-primary dark:text-primary-400" size={28} />
            </div>
            {t('bookingpage.selectDate')}
          </h2>
          <div className="flex items-center justify-between gap-3 md:gap-6">
            <button 
              onClick={() => changeDate(-1)}
              className="group w-12 h-12 md:w-14 md:h-14 flex-shrink-0 flex items-center justify-center glass-card hover:bg-primary/10 dark:hover:bg-primary/20 rounded-2xl transition-all duration-300 shadow-md hover:shadow-luxury border border-white/20 dark:border-neutral-700/30 hover:scale-110"
            >
              <ChevronLeft size={28} className="text-neutral-700 dark:text-neutral-300 group-hover:text-primary transition-colors" />
            </button>
            <div className="flex-1 text-center glass-card px-6 md:px-10 py-6 rounded-3xl border-2 border-primary/20 dark:border-primary/30 shadow-luxury hover:shadow-luxury-hover transition-all duration-500">
              <div className="text-xl md:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-1">
                {formatDate(selectedDate)}
              </div>
              {selectedDate.toDateString() === new Date().toDateString() && (
                <div className="inline-block px-4 py-1 bg-gradient-to-r from-primary/20 to-accent/20 rounded-full">
                  <span className="text-sm font-bold text-primary dark:text-primary-400">{t('bookingpage.today')}</span>
                </div>
              )}
            </div>
            <button 
              onClick={() => changeDate(1)}
              className="group w-12 h-12 md:w-14 md:h-14 flex-shrink-0 flex items-center justify-center glass-card hover:bg-primary/10 dark:hover:bg-primary/20 rounded-2xl transition-all duration-300 shadow-md hover:shadow-luxury border border-white/20 dark:border-neutral-700/30 hover:scale-110"
            >
              <ChevronRight size={28} className="text-neutral-700 dark:text-neutral-300 group-hover:text-primary transition-colors" />
            </button>
          </div>
        </div>

        {/* Time Slots - Luxury Glass Card */}
        <div className="glass-card rounded-3xl shadow-luxury p-8 md:p-10 border border-white/20 dark:border-neutral-700/30 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 dark:text-white flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-accent-400/20 to-primary-500/20 rounded-2xl flex items-center justify-center backdrop-blur-xl">
                <Clock className="text-accent dark:text-accent-400" size={28} />
              </div>
              {t('bookingpage.selectTime')}
            </h2>
            
            {/* Payment Mode Indicator */}
            {(() => {
                 const policy = stadiumSettings.cancellationPolicy || { type: 'pay-at-venue' };
                 const isPayAtVenue = policy.type === 'pay-at-venue' || !stadiumSettings.onlinePaymentRequired;
                 const isFullPayment = policy.type === 'full-payment';
                 
                 return (
                     <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${
                        isPayAtVenue 
                            ? 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400' 
                            : 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400'
                     }`}>
                         {isPayAtVenue ? <DollarSign size={16} /> : <CreditCard size={16} />}
                         <span className="text-sm font-bold uppercase tracking-wider">
                             {currentLang === 'ar' 
                                ? (isPayAtVenue ? 'الدفع في الملعب' : (isFullPayment ? 'دفع الكتروني كامل' : 'دفع عربون الكتروني'))
                                : (isPayAtVenue ? 'Pay at Venue' : (isFullPayment ? 'Full Online Payment' : 'Online Deposit'))
                             }
                         </span>
                     </div>
                 );
            })()}
          </div>
          
          {loading ? (
            <div className="text-center py-20">
              <div className="relative inline-block">
                <div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-6"></div>
                <div className="absolute inset-0 w-16 h-16 border-4 border-accent/20 border-b-accent rounded-full animate-spin-reverse"></div>
              </div>
              <div className="text-lg font-semibold text-neutral-600 dark:text-neutral-300">{t('bookingpage.loading')}</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {displaySlots.map((slot, index) => (
                <button
                  key={slot.id}
                  onClick={() => handleSlotClick(slot)}
                  disabled={slot.isBooked}
                  className={`group relative p-6 md:p-8 rounded-2xl border-2 transition-all duration-500 transform hover:scale-105 ${
                    slot.isBooked
                      ? 'bg-neutral-50/50 dark:bg-neutral-800/50 backdrop-blur-sm border-neutral-200 dark:border-neutral-700 cursor-not-allowed opacity-60'
                      : 'glass-card border-primary/30 dark:border-primary/40 hover:border-primary hover:shadow-luxury cursor-pointer'
                  }`}
                  style={{ animationDelay: `${0.5 + index * 0.05}s` }}
                >
                  {/* Luxury Gradient Background for Available Slots */}
                  {!slot.isBooked && (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-accent/5 to-yellow-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  )}
                  
                  <div className="relative flex flex-col items-center text-center">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 ${
                      slot.isBooked 
                        ? 'bg-neutral-200 dark:bg-neutral-700' 
                        : 'bg-gradient-to-br from-primary/20 to-accent/20 group-hover:scale-110'
                    }`}>
                      <Clock size={28} className={`${slot.isBooked ? 'text-neutral-400' : 'text-primary dark:text-primary-400'}`} />
                    </div>
                    <div className={`text-xl md:text-2xl font-bold mb-3 ${slot.isBooked ? 'text-neutral-500 dark:text-neutral-600' : 'text-neutral-800 dark:text-white'}`}>
                      {slot.startTime} - {slot.endTime}
                    </div>
                    <div className={`text-3xl font-bold mb-4 ${slot.isBooked ? 'text-neutral-400' : 'bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent'}`}>
                      ${slot.price}
                    </div>
                    <div className={`inline-block px-5 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                      slot.isBooked 
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800' 
                        : 'bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-700 group-hover:from-primary group-hover:to-accent group-hover:text-white group-hover:border-transparent'
                    }`}>
                      {slot.isBooked ? t('bookingpage.booked') : t('bookingpage.available')}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Luxury Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-[60] animate-fade-in overflow-y-auto">
          <div className="glass-card rounded-3xl border-2 border-white/20 dark:border-neutral-700/50 max-w-lg w-full p-6 md:p-8 animate-slide-up relative my-auto shadow-[0_20px_60px_0_rgba(0,0,0,0.3)] max-h-[90vh] overflow-y-auto bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl">

            {/* --- THIS IS THE FIX: The <form> wraps all states --- */}
            <form onSubmit={handleSubmit} className="relative z-10">
              {bookingState === 'form' && (
                <>
                  <button 
                    type="button"
                    onClick={handleCloseModal}
                    className={`absolute -top-2 ${currentLang === 'ar' ? 'left-0' : 'right-0'} p-3 text-neutral-500 dark:text-neutral-400 hover:bg-white/50 dark:hover:bg-neutral-700/50 backdrop-blur-xl rounded-full transition-all duration-300 hover:scale-110 border border-white/20 dark:border-neutral-700/30 shadow-md`}
                  >
                    <X size={24} />
                  </button>
                
                  <div className="text-center mb-6">
                    <div className="inline-block p-3 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl mb-3">
                      <CalendarIcon size={32} className="text-primary dark:text-primary-400" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-neutral-800 to-neutral-600 dark:from-white dark:to-neutral-300 bg-clip-text text-transparent mb-2">{t('bookingpage.bookNow')}</h2>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">{t('bookingpage.formTitle')}</p>
                  </div>
                  
                  {/* Luxury Slot Summary Card */}
                  <div className="relative glass-card p-4 md:p-5 rounded-2xl mb-5 border-2 border-primary/30 dark:border-primary/40 shadow-luxury overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/10 to-yellow-500/10"></div>
                    <div className="relative flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary/30 to-accent/30 rounded-xl flex items-center justify-center backdrop-blur-xl">
                          <Clock size={20} className="text-primary dark:text-primary-400" />
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-neutral-600 dark:text-neutral-300">{t('bookingpage.duration')}</div>
                          <div className="text-lg font-bold text-neutral-800 dark:text-white">{selectedSlot.startTime} - {selectedSlot.endTime}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-1">{t('bookingpage.total')}</div>
                        <div className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                          ${selectedSlot.price.toFixed(0)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Stadium-Specific Cancellation Policy Card */}
                  {(() => {
                    const policy = stadiumSettings.cancellationPolicy || {
                      type: 'pay-at-venue',
                      cancellationWindow: 36,
                      lateCancellationFee: 50,
                      refundPercentage: 100,
                      lateRefundPercentage: 0
                    };
                    
                    const bookingDateTime = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
                    const hoursUntil = calculateHoursUntilBooking(bookingDateTime, selectedSlot.startTime);
                    const deadline = getCancellationDeadline(bookingDateTime, selectedSlot.startTime, policy);
                    const isWithinWindow = hoursUntil > policy.cancellationWindow;
                    const isFullPayment = policy.type === 'full-payment';
                    const isDeposit = policy.type === 'partial-deposit';
                    
                    let title = currentLang === 'ar' ? 'سياسة الحجز والإلغاء' : 'Booking & Cancellation Policy';
                    let description = '';
                    let badge = '';

                    // Construct policy text
                    if (currentLang === 'ar') {
                      if (policy.type === 'pay-at-venue') {
                          badge = 'الدفع في الملعب';
                          description = `إلغاء مجاني قبل ${policy.cancellationWindow} ساعة من بدء الحجز.`;
                      } else if (isFullPayment) {
                          badge = 'دفع كامل';
                          description = isWithinWindow 
                            ? `يمكنك استرداد ${policy.refundPercentage}% من المبلغ إذا ألغيت قبل ${formatDeadline(deadline, 'ar')} (${policy.cancellationWindow} ساعة قبل الموعد).`
                            : `سيتم استرداد ${policy.lateRefundPercentage || 0}% فقط للإلغاء المتأخر (أقل من ${policy.cancellationWindow} ساعة قبل الموعد).`;
                      } else if (isDeposit) {
                         badge = `عربون ${policy.depositPercentage}%`;
                         description = isWithinWindow
                           ? `استرداد العربون (${policy.depositPercentage}%) متاح حتى ${formatDeadline(deadline, 'ar')} (${policy.cancellationWindow} ساعة قبل الموعد).`
                           : `العربون (${policy.depositPercentage}%) غير قابل للاسترداد للإلغاء المتأخر (أقل من ${policy.cancellationWindow} ساعة قبل الموعد).`;
                      }
                    } else {
                       if (policy.type === 'pay-at-venue') {
                          badge = 'Pay at Venue';
                          description = `Free cancellation up to ${policy.cancellationWindow} hours before kickoff.`;
                      } else if (isFullPayment) {
                          badge = 'Full Payment';
                          description = isWithinWindow 
                            ? `${policy.refundPercentage}% refund if cancelled before ${formatDeadline(deadline, 'en')} (${policy.cancellationWindow} hours before).`
                            : `Late cancellation: ${policy.lateRefundPercentage || 0}% refund (less than ${policy.cancellationWindow} hours before).`;
                      } else if (isDeposit) {
                         badge = `${policy.depositPercentage}% Deposit`;
                         description = isWithinWindow
                           ? `Deposit (${policy.depositPercentage}%) refundable until ${formatDeadline(deadline, 'en')} (${policy.cancellationWindow} hours before).`
                           : `Deposit (${policy.depositPercentage}%) non-refundable for late cancellation (less than ${policy.cancellationWindow} hours before).`;
                      }
                    }

                    return (
                      <div className={`relative glass-card p-4 rounded-2xl mb-5 border-2 shadow-md overflow-hidden ${
                        isWithinWindow 
                          ? 'border-green-300/50 dark:border-green-700/50' 
                          : 'border-orange-300/50 dark:border-orange-700/50'
                      }`}>
                        <div className={`absolute inset-0 ${
                          isWithinWindow 
                            ? 'bg-gradient-to-br from-green-50/50 to-emerald-50/50 dark:from-green-900/10 dark:to-emerald-900/10' 
                            : 'bg-gradient-to-br from-orange-50/50 to-yellow-50/50 dark:from-orange-900/10 dark:to-yellow-900/10'
                        }`}></div>
                        <div className="relative">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              isWithinWindow 
                                ? 'bg-green-100 dark:bg-green-900/30' 
                                : 'bg-orange-100 dark:bg-orange-900/30'
                            }`}>
                              <span className="text-xl">{isWithinWindow ? '✅' : '⚠️'}</span>
                            </div>
                            <div className="flex-1">
                              <h3 className={`text-sm font-bold mb-1 ${
                                isWithinWindow 
                                  ? 'text-green-700 dark:text-green-400' 
                                  : 'text-orange-700 dark:text-orange-400'
                              }`}>
                                {title}
                                {badge && (
                                  <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                                    {badge}
                                  </span>
                                )}
                              </h3>
                              <p className="text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed">
                                {description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {bookingError && (
                    <div className="glass-card border-2 border-red-400/50 dark:border-red-600/50 text-red-700 dark:text-red-300 px-4 py-3 rounded-2xl mb-4 text-center animate-shake shadow-luxury">
                      <div className="flex items-center justify-center gap-2">
                        <X size={18} />
                        <span className="font-semibold text-sm">{bookingError}</span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    
                    {/* Name Input */}
                    <div className="group">
                      <label className="block text-xs font-bold text-neutral-700 dark:text-neutral-200 mb-2">
                        {t('bookingpage.formFullName')} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className={`absolute ${currentLang === 'ar' ? 'right-3.5' : 'left-3.5'} top-1/2 transform -translate-y-1/2 z-10`}>
                          <User 
                            size={20} 
                            style={{ 
                              color: '#D4AF37',
                              stroke: '#D4AF37',
                              strokeWidth: 2.5,
                              filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))'
                            }}
                          />
                        </div>
                        <input
                          type="text"
                          value={bookingData.name}
                          onChange={(e) => setBookingData({...bookingData, name: e.target.value})}
                          disabled={!!(user?.name || user?.displayName)} 
                          className={`w-full ${currentLang === 'ar' ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 glass-card border-2 ${bookingError && !bookingData.name ? 'border-red-400 dark:border-red-500' : 'border-white/20 dark:border-neutral-700/50'} rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed hover:border-primary/50 text-neutral-800 dark:text-white shadow-md text-sm`}
                          placeholder={t('bookingpage.formFullName')}
                        />
                      </div>
                      {bookingError && !bookingData.name && (
                        <p className="text-red-500 text-xs mt-1 font-semibold animate-shake">
                          {currentLang === 'ar' ? 'يرجى إدخال الاسم الكامل' : 'Please enter your full name'}
                        </p>
                      )}
                    </div>

                    {/* Email Input */}
                    <div className="group">
                      <label className="block text-xs font-bold text-neutral-700 dark:text-neutral-200 mb-2">
                        {t('bookingpage.formEmail')} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className={`absolute ${currentLang === 'ar' ? 'right-3.5' : 'left-3.5'} top-1/2 transform -translate-y-1/2 z-10`}>
                          <Mail 
                            size={20} 
                            style={{ 
                              color: '#D4AF37',
                              stroke: '#D4AF37',
                              strokeWidth: 2.5,
                              filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))'
                            }}
                          />
                        </div>
                        <input
                          type="email"
                          value={bookingData.email}
                          onChange={(e) => setBookingData({...bookingData, email: e.target.value})}
                          disabled={!!user?.email} 
                          className={`w-full ${currentLang === 'ar' ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 glass-card border-2 ${bookingError && !bookingData.email ? 'border-red-400 dark:border-red-500' : 'border-white/20 dark:border-neutral-700/50'} rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed hover:border-primary/50 text-neutral-800 dark:text-white shadow-md text-sm`}
                          placeholder="example@mail.com"
                        />
                      </div>
                      {bookingError && !bookingData.email && (
                        <p className="text-red-500 text-xs mt-1 font-semibold animate-shake">
                          {currentLang === 'ar' ? 'يرجى إدخال البريد الإلكتروني' : 'Please enter your email'}
                        </p>
                      )}
                    </div>

                    {/* Phone Input */}
                    <div className="group">
                      <label className="block text-xs font-bold text-neutral-700 dark:text-neutral-200 mb-2">
                        {t('bookingpage.formPhone')}
                      </label>
                      <div className="relative">
                        <div className={`absolute ${currentLang === 'ar' ? 'right-3.5' : 'left-3.5'} top-1/2 transform -translate-y-1/2 z-10`}>
                          <Phone 
                            size={20} 
                            style={{ 
                              color: '#D4AF37',
                              stroke: '#D4AF37',
                              strokeWidth: 2.5,
                              filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))',
                              opacity: 0.6
                            }}
                          />
                        </div>
                        <input
                          type="tel"
                          value={bookingData.phone}
                          disabled 
                          className={`w-full ${currentLang === 'ar' ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 glass-card border-2 border-white/20 dark:border-neutral-700/50 rounded-xl opacity-60 cursor-not-allowed text-neutral-800 dark:text-white shadow-md text-sm`}
                          placeholder="03 123 456"
                        />
                      </div>
                    </div>

                    {/* Players Input */}
                    <div className="group">
                      <label className="block text-xs font-bold text-neutral-700 dark:text-neutral-200 mb-2">
                        {t('bookingpage.formPlayers')} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className={`absolute ${currentLang === 'ar' ? 'right-3.5' : 'left-3.5'} top-1/2 transform -translate-y-1/2 z-10`}>
                          <Users 
                            size={20} 
                            style={{ 
                              color: '#D4AF37',
                              stroke: '#D4AF37',
                              strokeWidth: 2.5,
                              filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))'
                            }}
                          />
                        </div>
                        <input
                          type="number"
                          min="1"
                          value={bookingData.players}
                          onChange={(e) => setBookingData({...bookingData, players: e.target.value})}
                          className={`w-full ${currentLang === 'ar' ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 glass-card border-2 ${bookingError && !bookingData.players ? 'border-red-400 dark:border-red-500' : 'border-white/20 dark:border-neutral-700/50'} rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 hover:border-primary/50 text-neutral-800 dark:text-white shadow-md text-sm placeholder-neutral-400 dark:placeholder-neutral-500`}
                          placeholder={currentLang === 'ar' ? "مثال: 10" : "e.g., 10"}
                          style={{
                            WebkitTextFillColor: bookingData.players ? undefined : 'var(--tw-placeholder-color)'
                          }}
                        />
                      </div>
                      {bookingError && !bookingData.players && (
                        <p className="text-red-500 text-xs mt-1 font-semibold animate-shake">
                          {currentLang === 'ar' ? 'يرجى إدخال عدد اللاعبين' : 'Please enter number of players'}
                        </p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={handleCloseModal}
                        className="flex-1 px-5 py-3 glass-card border-2 border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-200 rounded-xl font-bold hover:bg-white/50 dark:hover:bg-neutral-700/50 transition-all duration-300 shadow-md hover:shadow-luxury hover:scale-105 text-sm"
                      >
                        {t('bookingpage.formCancel')}
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-5 py-3 bg-gradient-to-r from-primary to-accent text-white rounded-xl font-bold hover:shadow-luxury-hover transition-all duration-300 shadow-luxury hover:scale-105 transform text-sm"
                      >
                        {stadiumSettings.onlinePaymentRequired ? t('bookingpage.proceedToPayment') : t('bookingpage.formConfirm')}
                      </button>
                    </div>
                  </div>
                </>
              )}

            </form>

              {bookingState === 'payment' && (
                <div className="text-center py-8">
                    <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                        <span className="text-4xl">💳</span>
                    </div>
                    <h3 className="text-2xl font-bold text-neutral-800 dark:text-white mb-2">{t('bookingpage.paymentRequired', 'Online Payment Required')}</h3>
                    
                    {clientSecret ? (
                        <div className="mt-6 text-left p-4 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 shadow-sm">
                            <Elements options={{ clientSecret, appearance: { theme: 'stripe' } }} stripe={stripePromise}>
                                <CheckoutForm 
                                    amount={(() => {
                                      const policy = stadiumSettings.cancellationPolicy || {};
                                      const isDeposit = policy.type === 'partial-deposit';
                                      const amount = isDeposit && policy.depositPercentage 
                                          ? Math.ceil(selectedSlot.price * (policy.depositPercentage / 100)) 
                                          : selectedSlot.price;
                                      return amount.toFixed(0);
                                    })()} 
                                    onSuccess={handlePaymentSuccess}
                                    onCancel={() => setBookingState('form')} 
                                />
                            </Elements>
                        </div>
                    ) : (
                        <div className="py-10">
                            <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-neutral-500">{t('bookingpage.loadingPayment', 'Loading secure payment...')}</p>
                        </div>
                    )}
                </div>
              )}

              {bookingState === 'submitting' && (
                <div className="text-center py-20">
                  <div className="relative inline-block mb-8">
                    <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                    <div className="absolute inset-0 w-10 h-10 border-4 border-accent/20 border-b-accent rounded-full animate-spin-reverse"></div>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-neutral-800 to-neutral-600 dark:from-white dark:to-neutral-300 bg-clip-text text-transparent mb-2">
                    {t('bookingpage.submitting')}
                  </h2>
                  <p className="text-neutral-600 dark:text-neutral-300">
                    {t('bookingpage.pleaseWait', 'Please wait...')}
                  </p>
                </div>
              )}

              {bookingState === 'success' && (
                <div className="text-center py-8">
                  <div className="inline-block p-6 bg-gradient-to-br from-primary-400/20 to-accent-500/20 rounded-full mb-6 animate-scale-up">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-600 rounded-full flex items-center justify-center shadow-lg shadow-primary-500/30">
                      <Check size={40} className="text-white" strokeWidth={3} />
                    </div>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-neutral-800 to-neutral-600 dark:from-white dark:to-neutral-300 bg-clip-text text-transparent mb-4 animate-fade-in-up">
                    {t('bookingpage.successTitle')}
                  </h2>
                  <p className="text-neutral-600 dark:text-neutral-300 mb-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                    {t('bookingpage.successMessage')}
                  </p> 
                  <button
                    onClick={handleCloseModal}
                    className="w-full py-4 bg-gradient-to-r from-primary to-accent text-white rounded-xl font-bold hover:shadow-luxury-hover transition-all duration-300 shadow-luxury hover:scale-105 transform animate-fade-in-up"
                    style={{ animationDelay: '0.2s' }}
                  >
                    {t('bookingpage.successButton')}
                  </button>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingPage;